import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PROTECTION_STATES } from '@ward/core';
import { api, qs, useAction, useApi } from '../api';
import { useCan } from '../auth';
import { ConfirmDialog } from '../ui/confirm';
import { Ago, AlertStatus, browserLabel, Button, clickableRow, Empty, EntityLink, ExtStatus, Figure, Figures, fmtDate, ListToolbar, Menu, MenuItem, MutationError, osLabel, PageHead, PAGE_SIZE, Pager, Protection, PROTECTION_LABEL, Q, Risk, Section, Severity, SortTh, statusTone, Tag, useFilters } from '../ui/kit';
import { PROTECTION } from '../ui/format';
import { EventTable, useEventDrawer } from './Activity';
import { ProtectionHealthFacts } from './protect/health';
import { DeviceDiagnosticsButton } from './system/Diagnostics';

/** Identity trust state: only "Verified" earns identity-based relaxations; restrictions apply in every state. */
const TRUST: Record<string, [string, string, string]> = {
  verified: ['Verified', 'allow', 'Identity-scoped exceptions apply'],
  unverified: ['Unverified', 'warn', 'Reported by the browser; identity-scoped exceptions do not apply until the user signs in with Entra ID or an administrator confirms it'],
  verified_stale: ['Verification expired', 'warn', 'The Entra sign-in is older than the trust lifetime; exceptions stop until the device re-verifies'],
  verification_failed: ['Verification failed', 'block', 'The latest sign-in evidence was rejected; exceptions stop until a fresh valid verification'],
  user_disabled: ['User disabled', 'block', 'The directory user is disabled; exceptions no longer apply'],
};
function TrustTag({ t }: { t?: { state: string; expiresAt: string | null } }) {
  const [label, tone, why] = TRUST[t?.state ?? 'unverified'] ?? TRUST.unverified!;
  return <Tag tone={tone} title={why + (t?.expiresAt ? ` (until ${fmtDate(t.expiresAt)})` : '')}>{label}</Tag>;
}

const VERIFIED_BY: Record<string, string> = { entra: 'by Entra ID sign-in', admin: 'confirmed by an administrator' };
const IDENTITY_ERROR: Record<string, string> = {
  unknown_user: 'signed-in user is not in the directory', user_disabled: 'user is disabled in the directory', user_mismatch: 'UPN belongs to a different Entra object',
  wrong_issuer: 'token from another tenant', wrong_tenant: 'token from another tenant', wrong_audience: 'token for a different app', expired: 'token expired',
  stale: 'token too old', bad_signature: 'invalid signature', bad_nonce: 'nonce mismatch (possible replay)', no_challenge: 'no active challenge (possible replay)',
  missing_claims: 'token missing required claims', malformed: 'malformed token', keys_unavailable: 'Entra signing keys unavailable',
};

const majorVersion = (v: string | null | undefined) => (v ? v.split('.')[0] : '');

export function Devices() {
  const filters = useFilters();
  const { sp, set, offset, setOffset } = filters;
  const nav = useNavigate();
  const can = useCan();
  const state = sp.get('state') ?? '';
  const q = useApi<{ total: number; items: any[]; states: any[] }>(`/devices${qs({ state, q: sp.get('q'), sort: sp.get('sort'), limit: PAGE_SIZE, offset })}`);
  return (
    <>
      <PageHead
        title="Devices"
        sub="Every enrolled browser, its protection state and last contact. A device is one browser installation; its name comes from MDM configuration or an administrator."
        actions={can('deployment:write') ?<Link className="btn primary" to="/admin/deployment">Enroll devices</Link> : undefined}
      />
      <Q q={q}>
        {(d) => (
          <>
            <Figures>
              {PROTECTION_STATES.map((s) => {
                const n = d.states.find((x) => x.protection_state === s)?.n ?? 0;
                return <Figure key={s} label={PROTECTION_LABEL(s)} value={n} tone={n && s !== 'protected' ? statusTone(PROTECTION, s) : undefined} onClick={() => set('state', state === s ? '' : s)} pressed={state === s} />;
              })}
            </Figures>
            <ListToolbar filters={filters} placeholder="Search device or user…" />
            {d.items.length === 0 ? <Empty title="No devices">{filters.active() ? 'No device matches these filters.' : <>Enroll browsers under <Link to="/admin/deployment">Deployment</Link>.</>}</Empty> : (
              <div className="table-wrap"><table className="t">
                <thead><tr><th>Device</th><th>User</th><th>Status</th><SortTh filters={filters} col="last_heartbeat_at">Last heartbeat</SortTh></tr></thead>
                <tbody>{d.items.map((x) => (
                  <tr key={x.id} {...clickableRow(() => nav(`/discover/devices/${x.id}`))}>
                    <td>
                      <EntityLink to={`/discover/devices/${x.id}`}>{x.label}</EntityLink>
                      <div className="sub">{osLabel(x.os)} · {browserLabel(x.browser)} {majorVersion(x.browser_version)} · {x.managed ? 'managed' : 'unmanaged'}</div>
                    </td>
                    <td>{x.user_name ?? <span className="sub">{x.reported_email ?? '—'}</span>}{x.user_name && <div className="sub">{x.user_verified ? (x.user_verified_method === 'entra' ? 'verified · Entra sign-in' : 'verified · admin') : 'unverified'}</div>}</td>
                    <td><Protection s={x.revoked_at ? 'unprotected' : x.protection_state} /><div className="sub">{x.revoked_at ? 'enrollment revoked' : x.health?.reason}</div></td>
                    <td className="sub"><Ago at={x.last_heartbeat_at} /></td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
            <Pager total={d.total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
          </>
        )}
      </Q>
    </>
  );
}

/** The device name as the page title; writers rename it in place. */
function DeviceTitle({ id, label, writable }: { id: string; label: string; writable: boolean }) {
  const [draft, setDraft] = useState<string | null>(null);
  const rename = useAction((l: string) => api(`/devices/${id}`, { method: 'PATCH', body: { label: l } }), () => setDraft(null));
  if (draft === null) {
    return <>{label}{writable && <button type="button" className="btn-inline" onClick={() => { rename.reset(); setDraft(label); }}>Rename</button>}</>;
  }
  return (
    <form className="title-edit" onSubmit={(e) => { e.preventDefault(); if (draft.trim()) rename.mutate(draft.trim()); }}>
      <input aria-label="Device name" value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={100} autoFocus />
      <Button type="submit" variant="primary" size="small" disabled={!draft.trim() || rename.isPending}>Save</Button>
      <Button size="small" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
      <MutationError error={rename.error} />
    </form>
  );
}

export function DeviceDetail() {
  const { id } = useParams();
  const can = useCan();
  const writable = can('devices:write');
  const q = useApi<any>(`/devices/${id}`);
  const ev = useEventDrawer();
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [assign, setAssign] = useState<string>('');
  const users = useApi<any[]>(writable ? '/users/options' : null);
  const verify = useAction((userId: string | null) => api(`/devices/${id}`, { method: 'PATCH', body: { userId } }));
  const revoke = useAction(() => api(`/devices/${id}/revoke`, { method: 'POST' }), () => setConfirmRevoke(false));
  return (
    <Q q={q}>
      {({ device: d, extensions, alerts, recentEvents }) => (
        <>
          <PageHead
            crumbs={<Link to="/discover/devices">Devices</Link>}
            title={<DeviceTitle id={d.id} label={d.label} writable={writable} />}
            sub={<>{osLabel(d.os)} {d.arch} · {browserLabel(d.browser)} {d.browser_version} · {d.managed ? 'managed by MDM' : 'not MDM-managed'}</>}
            actions={<>
              <Protection s={d.revoked_at ? 'unprotected' : d.protection_state} />
              <Link className="btn" to={`/investigate/activity?device=${d.id}`}>View activity</Link>
              {writable && !d.revoked_at && <Menu label="More">
                <MenuItem danger onClick={() => { revoke.reset(); setConfirmRevoke(true); }}>Revoke enrollment…</MenuItem>
              </Menu>}
            </>}
          />
          <div className="cols cols-2-1">
            <div>
              <dl className="facts">
                <dt>Status</dt><dd>{d.revoked_at ? `Enrollment revoked ${fmtDate(d.revoked_at)}` : d.health?.reason ?? '—'}</dd>
                <dt>User</dt><dd>{d.user_id ? <Link to={`/discover/users/${d.user_id}`}>{d.user_name}</Link> : 'Not matched to a directory user'}{' '}
                  {d.user_id && <TrustTag t={d.identity_trust} />}
                  {d.user_id && d.user_verified && <span className="sub"> {VERIFIED_BY[d.user_verified_method] ?? ''}{d.user_verified_at && <> · <Ago at={d.user_verified_at} /></>}</span>}
                  {d.identity_check_error && <div className="sub text-block" title="A failed sign-in does not remove an existing verification">Last Entra sign-in check failed{d.identity_checked_at && <> <Ago at={d.identity_checked_at} /></>}: {IDENTITY_ERROR[d.identity_check_error] ?? d.identity_check_error}</div>}
                  {d.reported_email && <div className="sub">browser reports {d.reported_email}</div>}</dd>
                <dt>Extension</dt><dd>{d.extension_version}</dd>
                <dt>Policy</dt><dd>{d.policy_version !== null ? `v${d.policy_version}, activated ${fmtDate(d.policy_activated_at)}` : 'none active'} <span className="sub">· last fetched <Ago at={d.policy_fetched_at} /></span></dd>
                <dt>Last heartbeat</dt><dd>{fmtDate(d.last_heartbeat_at)} <span className="sub">(<Ago at={d.last_heartbeat_at} />)</span></dd>
                <dt>Last telemetry</dt><dd>{fmtDate(d.last_telemetry_at)}</dd>
                <dt>Queue</dt><dd>{d.health?.queueDepth ?? 0} pending events on device{d.health?.droppedEvents ? ` · ${d.health.droppedEvents} dropped (queue full)` : ''}</dd>
                <dt>Permissions</dt><dd className="sub">{(d.health?.grantedPermissions ?? []).join(', ') || '—'}<br />Site access: {(d.health?.grantedOrigins ?? []).join(', ') || 'none'}</dd>
                {d.health?.lastError && (<><dt>Last error</dt><dd>{d.health.lastError}</dd></>)}
                <ProtectionHealthFacts p={d.health?.protection} />
                <dt>Enrolled</dt><dd>{fmtDate(d.enrolled_at)} {d.enrollment_token_name && <span className="sub">with token “{d.enrollment_token_name}”</span>}</dd>
              </dl>
              <DeviceDiagnosticsButton deviceId={d.id} />
            </div>
            {writable && (
              <Section title="Assigned user">
                <div className="stack-8 mt-12">
                  <label className="field">User <span className="hint">Confirming the user lets identity-scoped exceptions apply to this device</span>
                    <select value={assign || d.user_id || ''} onChange={(e) => setAssign(e.target.value)}>
                      <option value="">— none —</option>
                      {users.data?.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                    </select>
                  </label>
                  <div className="actions">
                    <Button disabled={!(assign || d.user_id) || (d.user_verified && (assign || d.user_id) === d.user_id)} onClick={() => verify.mutate(assign || d.user_id)}>{d.user_verified ? 'Change user' : 'Confirm user'}</Button>
                    {d.user_id && <Button variant="ghost" onClick={() => verify.mutate(null)}>Clear</Button>}
                  </div>
                  <MutationError error={verify.error} />
                </div>
              </Section>
            )}
          </div>
          <div className="cols cols-2">
            <Section title="Installed extensions">
              {extensions.length === 0 ? <Empty title="No inventory reported yet" /> : (
                <table className="t"><tbody>{extensions.map((e: any) => <tr key={e.id}><td><Link to={`/discover/extensions/${e.id}`}>{e.name}</Link><div className="sub">{e.version} · {e.install_type}{!e.enabled && ' · disabled'}</div></td><td><Risk r={e.risk_level} /></td><td><ExtStatus s={e.status} /></td></tr>)}</tbody></table>
              )}
            </Section>
            <Section title="Alerts">
              {alerts.length === 0 ? <Empty title="No alerts" /> : (
                <table className="t"><tbody>{alerts.map((a: any) => <tr key={a.id}><td><Severity s={a.severity} /></td><td><Link to={`/investigate/alerts/${a.id}`}>{a.title}</Link></td><td><AlertStatus s={a.status} /></td></tr>)}</tbody></table>
              )}
            </Section>
          </div>
          <Section title="Recent security events" aside={<Link to={`/investigate/activity?device=${d.id}`}>View all activity</Link>}><EventTable rows={recentEvents} onOpen={ev.open} compact /></Section>
          {ev.drawer}
          {confirmRevoke && (
            <ConfirmDialog title={`Revoke ${d.label}?`} confirmLabel="Revoke enrollment" danger busy={revoke.isPending} error={revoke.error}
              onConfirm={() => revoke.mutate(undefined)} onClose={() => setConfirmRevoke(false)}>
              <p>Revoking stops this browser from receiving policy updates or sending telemetry. Its last policy keeps enforcing locally until the extension is removed or re-enrolled.</p>
            </ConfirmDialog>
          )}
        </>
      )}
    </Q>
  );
}
